import { Howler } from "howler";
import type { Sfx } from "./Sfx";

/**
 * A sparse procedural drone that follows how much danger you're in. Calm, it
 * is a couple of detuned low voices through a slowly breathing low-pass, with
 * now and then a soft high note. As danger rises the filter opens, a low
 * heartbeat-like pulse fades in and a dissonant voice creeps in under it.
 *
 * Built straight on the Web Audio context Howler already made, routed through
 * Howler's master gain so the master slider and mute apply too. Nothing is
 * made until that context exists and is running (browsers keep it suspended
 * until the first click or key), and any failure just leaves it silent.
 */

/** Root of the drone (Hz): a low A. */
const ROOT = 55;
/** Soft high notes, an A minor pentatonic a few octaves up, picked at random. */
const NOTES = [440, 523.25, 587.33, 659.25, 783.99, 880];

/** How often (s) the drone's levels are re-aimed at the danger. */
const AIM_EVERY = 0.25;

/** Head `param` toward `value` from now, dropping whatever was still scheduled so events never pile up. */
function glide(param: AudioParam, value: number, now: number, timeConstant: number): void {
  const held = param.value;
  param.cancelScheduledValues(now);
  param.setValueAtTime(held, now);
  param.setTargetAtTime(value, now, timeConstant);
}

interface Nodes {
  ctx: AudioContext;
  /** Follows the music slider and mute. */
  out: GainNode;
  filter: BiquadFilterNode;
  drone: GainNode;
  /** The dissonant voice (a minor second over the root), up with danger. */
  clash: GainNode;
  /** The low pulse; its gain is struck in a lub-dub rhythm. */
  pulse: GainNode;
}

export class Music {
  private nodes?: Nodes;
  /** Gave up (no Web Audio, or something threw): stay silent for good. */
  private broken = false;
  /** Danger, eased so nothing jumps. */
  private level = 0;
  /** Context time of the next pulse beat. */
  private nextBeat = 0;
  /** Seconds until the next soft high note. */
  private noteIn = 6;
  /** Seconds since the levels were last re-aimed. */
  private sinceAim = AIM_EVERY;

  constructor(private readonly sfx: Sfx) {}

  /** Every frame. `danger`: 0 calm .. 1 deadly. */
  update(dt: number, danger: number): void {
    if (this.broken) return;
    try {
      this.level += (Math.max(0, Math.min(1, danger)) - this.level) * (1 - Math.exp(-dt * 0.6));
      const n = this.nodes ?? this.start();
      if (!n) return;
      const now = n.ctx.currentTime;
      const d = this.level;
      // Re-aimed a few times a second, not every frame: each call is an automation event the audio
      // thread has to work through, and left to pile up at 60 a second they slow everything down.
      this.sinceAim += dt;
      if (this.sinceAim >= AIM_EVERY) {
        this.sinceAim = 0;
        // Slow time constants, so every change glides.
        glide(n.out.gain, this.sfx.muted ? 0 : this.sfx.musicVolume * 0.5, now, 0.3);
        glide(n.filter.frequency, 260 + 1400 * d * d + 300 * d, now, 1.5);
        glide(n.filter.Q, 1 + 5 * d, now, 1.5);
        glide(n.drone.gain, 0.16 + 0.06 * d, now, 1.5);
        glide(n.clash.gain, (0.1 * Math.max(0, d - 0.2)) / 0.8, now, 2);
      }
      this.beats(n, now, d);
      this.notes(n, now, dt, d);
    } catch {
      this.broken = true;
      this.stop();
    }
  }

  /** Make the nodes once Howler's context is up and running. */
  private start(): Nodes | undefined {
    const ctx = Howler.ctx as AudioContext | undefined;
    if (!Howler.usingWebAudio || !ctx || !Howler.masterGain) {
      // Howler makes its context on first use; until then there's simply nothing to build on.
      return undefined;
    }
    if (ctx.state !== "running") return undefined;
    const out = ctx.createGain();
    out.gain.value = 0;
    out.connect(Howler.masterGain);

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 260;
    filter.Q.value = 1;
    filter.connect(out);
    // The filter breathes: a very slow wobble on its cutoff.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.04;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 140;
    lfo.connect(lfoDepth).connect(filter.frequency);
    lfo.start();

    const drone = ctx.createGain();
    drone.gain.value = 0;
    drone.connect(filter);
    // Root, a hair-detuned root (they beat slowly against each other) and a quiet fifth.
    for (const [f, type, gain] of [
      [ROOT, "sawtooth", 1],
      [ROOT * 1.004, "sawtooth", 0.8],
      [ROOT * 1.5, "triangle", 0.5],
    ] as const) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = gain;
      o.connect(g).connect(drone);
      o.start();
    }

    const clash = ctx.createGain();
    clash.gain.value = 0;
    clash.connect(filter);
    const c = ctx.createOscillator();
    c.type = "sawtooth";
    c.frequency.value = ROOT * 2 * 1.0595; // a minor second over the octave: the uneasy one
    c.connect(clash);
    c.start();

    // The pulse skips the filter: it's low enough already, and should stay a thump.
    const pulse = ctx.createGain();
    pulse.gain.value = 0;
    pulse.connect(out);
    const p = ctx.createOscillator();
    p.type = "triangle";
    p.frequency.value = ROOT * 0.9;
    p.connect(pulse);
    p.start();

    this.nodes = { ctx, out, filter, drone, clash, pulse };
    this.nextBeat = ctx.currentTime + 0.5;
    return this.nodes;
  }

  /** Strike the pulse a little ahead of time: lub-dub, quicker and louder the worse it gets. Silent when calm. */
  private beats(n: Nodes, now: number, d: number): void {
    if (this.nextBeat < now) this.nextBeat = now + 0.05;
    const loud = 0.5 * Math.max(0, d - 0.1) / 0.9;
    const period = 60 / (48 + 62 * d);
    while (this.nextBeat < now + 0.2) {
      const t = this.nextBeat;
      if (loud > 0.005) {
        const g = n.pulse.gain;
        g.setTargetAtTime(loud, t, 0.01);
        g.setTargetAtTime(0, t + 0.07, 0.06);
        g.setTargetAtTime(loud * 0.6, t + 0.24, 0.01);
        g.setTargetAtTime(0, t + 0.3, 0.07);
      }
      this.nextBeat += period;
    }
  }

  /** Now and then, while it's calm, a soft high note swells and fades. */
  private notes(n: Nodes, now: number, dt: number, d: number): void {
    this.noteIn -= dt;
    if (this.noteIn > 0) return;
    this.noteIn = 9 + Math.random() * 14;
    if (d > 0.5) return;
    const o = n.ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = NOTES[Math.floor(Math.random() * NOTES.length)]!;
    const g = n.ctx.createGain();
    g.gain.value = 0;
    g.gain.setTargetAtTime(0.035 * (1 - d * 2), now, 0.8);
    g.gain.setTargetAtTime(0, now + 2.5, 1.5);
    o.connect(g).connect(n.out);
    o.start(now);
    o.stop(now + 10);
    o.onended = () => g.disconnect();
  }

  private stop(): void {
    try {
      this.nodes?.out.disconnect();
    } catch {
      // Already gone.
    }
    this.nodes = undefined;
  }
}
